import React, { useCallback, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useChatToken } from './hooks/useChatToken';
import { useChatConnection, ConnectionStatus } from './hooks/useChatConnection';
import { useChatRoom } from './hooks/useChatRoom';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ChatStatus from './ChatStatus';
import ChatNickname from './ChatNickname';

const i18n = defineMessages({
  liveChat: { id: 'liveChatRegion', defaultMessage: 'Live chat' },
  noLongerPresent: {
    id: 'liveChatNoLongerPresent',
    defaultMessage: 'You are no longer present in this chat.',
  },
  muted: { id: 'liveChatMuted', defaultMessage: 'You are muted.' },
  joinError: {
    id: 'liveChatJoinError',
    defaultMessage: 'Could not join the chat. Please try reloading the page.',
  },
  nickChanged: {
    id: 'liveChatNickChanged',
    defaultMessage: 'Name was already taken. You joined as {nick}.',
  },
});

/**
 * LiveChat — embedded Strophe.js chat panel for live media pages.
 * Orchestrates token fetching, XMPP connection, and room interaction.
 */
const LiveChat = ({ contentPath }) => {
  const intl = useIntl();
  // Client-only guard: prevents hydration mismatch since hooks use browser APIs
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const {
    token,
    loading,
    error,
    needsNickname,
    postNickname,
    retryToken,
    latestPasswordRef,
  } = useChatToken(contentPath);

  const { status, connection, retry } = useChatConnection(
    token?.websocket_url,
    token?.jid,
    token?.password,
    latestPasswordRef,
  );

  const roomJid = token?.rooms?.[0];
  const nickname = token?.nickname || token?.jid?.split('@')[0] || '';

  const {
    messages,
    occupantCount,
    occupantRoles,
    ownRole,
    kicked,
    joinError,
    effectiveNickname,
    sendMessage,
  } = useChatRoom(connection, roomJid, nickname, status);

  // Subtract 1 for the bot occupant (Valvoja)
  const visibleOccupants = Math.max(0, occupantCount - 1);
  const isModerator = ownRole === 'moderator';
  const isMuted = ownRole === 'visitor';

  // Moderation via groupchat bot commands (intercepted before broadcast)
  const handleModAction = useCallback(
    async (action, target) => {
      if (!connection || !roomJid) return;
      const { $msg } = await import('strophe.js');
      let command;
      switch (action) {
        case 'retract':
          command = `!retract ${target}`;
          break;
        case 'clearAll':
          command = '!clear';
          break;
        case 'clear':
          command = `!clear ${target}`;
          break;
        case 'mute':
          command = `!mute ${target}`;
          break;
        case 'unmute':
          command = `!unmute ${target}`;
          break;
        case 'remove':
          command = `!remove ${target}`;
          break;
        default:
          return;
      }
      // Send as groupchat — bot intercepts before broadcast (invisible to room)
      const msg = $msg({ to: roomJid, type: 'groupchat' }).c('body').t(command);
      connection.send(msg);
    },
    [connection, roomJid],
  );

  // Loading state (or SSR — show placeholder to match server output)
  if (!mounted || loading) {
    return (
      <div
        className="live-chat-panel"
        role="region"
        aria-label={intl.formatMessage(i18n.liveChat)}
      >
        <ChatHeader occupantCount={0} />
        <ChatStatus status="CONNECTING" />
      </div>
    );
  }

  // Token error
  if (error) {
    return (
      <div
        className="live-chat-panel"
        role="region"
        aria-label={intl.formatMessage(i18n.liveChat)}
      >
        <ChatHeader occupantCount={0} />
        <ChatStatus status="ERROR" onRetry={retryToken} />
      </div>
    );
  }

  // Anonymous — needs nickname
  if (needsNickname) {
    return (
      <div
        className="live-chat-panel"
        role="region"
        aria-label={intl.formatMessage(i18n.liveChat)}
      >
        <ChatHeader occupantCount={0} />
        <ChatNickname onJoin={postNickname} />
      </div>
    );
  }

  // Connecting / disconnected / error
  if (status !== ConnectionStatus.CONNECTED) {
    return (
      <div
        className="live-chat-panel"
        role="region"
        aria-label={intl.formatMessage(i18n.liveChat)}
      >
        <ChatHeader occupantCount={0} />
        <ChatStatus status={status} onRetry={retry} />
      </div>
    );
  }

  // Join error (e.g. all nick retries exhausted)
  if (joinError) {
    return (
      <div
        className="live-chat-panel"
        role="region"
        aria-label={intl.formatMessage(i18n.liveChat)}
      >
        <ChatHeader occupantCount={0} />
        <div className="live-chat-status">
          <span>{intl.formatMessage(i18n.joinError)}</span>
        </div>
      </div>
    );
  }

  // Kicked — show "no longer present" state
  if (kicked) {
    return (
      <div
        className="live-chat-panel"
        role="region"
        aria-label={intl.formatMessage(i18n.liveChat)}
      >
        <ChatHeader occupantCount={0} />
        <div className="live-chat-status">
          <span>{intl.formatMessage(i18n.noLongerPresent)}</span>
        </div>
      </div>
    );
  }

  // Nickname that was actually joined (may differ from requested if conflict)
  const nickWasChanged = effectiveNickname && effectiveNickname !== nickname;

  // Connected — show chat
  return (
    <div
      className="live-chat-panel"
      role="region"
      aria-label={intl.formatMessage(i18n.liveChat)}
    >
      <ChatHeader
        occupantCount={visibleOccupants}
        isModerator={isModerator}
        onClearAll={() => handleModAction('clearAll')}
      />
      {nickWasChanged && (
        <div className="live-chat-message live-chat-message-system">
          <span className="live-chat-message-system-text">
            {intl.formatMessage(i18n.nickChanged, { nick: effectiveNickname })}
          </span>
        </div>
      )}
      <ChatMessages
        messages={messages}
        isModerator={isModerator}
        onModAction={handleModAction}
        occupantRoles={occupantRoles}
      />
      {isMuted ? (
        <div className="live-chat-muted-notice">
          {intl.formatMessage(i18n.muted)}
        </div>
      ) : (
        <ChatInput onSend={sendMessage} disabled={false} />
      )}
    </div>
  );
};

export default LiveChat;
